'use client';

import { bunja } from 'bunja';
import { keyBy } from 'es-toolkit';
import { atom } from 'jotai';
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';

import {
  Currency,
  Instrument,
  InstrumentApi,
  InstrumentProductTypeEnum,
  SpecSnapshot,
  getSpecSnapshotApi,
} from '@flipster/apis/v2/snapshot';
import { pswapsBunja } from '@flipster/socket/r230522/pswaps';
import { alert } from '@flipster/utils/alert';
import { BonusCurrency, UsdtCurrency } from '@flipster/utils/constants/currency';
import Num, { countDecimalPlaces } from '@flipster/utils/num';

import { checkListableInstrument } from './utils';

export type CurrencyV2InstanceType = ReturnType<typeof currencyV2Instance>;
export type InstrumentV2InstanceType = ReturnType<typeof instrumentV2Instance>;
export type InstrumentV2ForApiInstanceType = ReturnType<typeof instrumentV2ForApiInstance>;

const currencyV2Instance = (c: Currency) => {
  const isUSDT = c.currency === UsdtCurrency;
  const isBonus = c.currency === BonusCurrency;

  return {
    ...c,
    isUSDT,
    isBonus,
    englishName: isBonus ? 'USDT Bonus' : c.englishName,
    conversionUnit: c.conversionUnit ?? '0.0001',
    getMinConfirmation(networkName: string) {
      const blockchainInfo = c.blockchainInfo.find((bi) => bi.networkName === networkName) ?? c.blockchainInfo[0];
      return blockchainInfo.minConfirmations;
    },
  };
};

const instrumentV2Instance = <I extends Instrument>(i: I) => {
  return {
    ...i,
    currency: i.positionUnit,
    isSpot: i.productType === InstrumentProductTypeEnum.Spot,
    isFutures: i.productType === InstrumentProductTypeEnum.Pswap,
  };
};

const instrumentV2ForApiInstance = (i: InstrumentApi) => {
  const maxLeverageHardLimit = Math.round(Num(1).dividedBy(i.initMarginRate).toNumber());
  // unitOrderQty = lot size
  const tradeQtyPrecision = countDecimalPlaces(i.unitOrderQty);

  return {
    ...instrumentV2Instance(i),
    maxLeverageHardLimit,
    tradeQtyPrecision,
  };
};

export type SnapshotSpec = ReturnType<typeof snapshotSpecResolver>;
export const snapshotSpecResolver = (data: SpecSnapshot) => {
  const instrumentsInstance: InstrumentV2ForApiInstanceType[] = Object.values(data.t.instruments.s).map(
    instrumentV2ForApiInstance,
  );
  // array list 의 경우는 딱 listing 가능한 것들만 내려주기
  const pswapInstruments = instrumentsInstance.filter((i) =>
    checkListableInstrument(i, InstrumentProductTypeEnum.Pswap),
  );
  const spotInstruments = instrumentsInstance.filter((i) => checkListableInstrument(i, InstrumentProductTypeEnum.Spot));
  const instrumentsKeyBySymbol: Record<string, InstrumentV2ForApiInstanceType> = keyBy(
    instrumentsInstance,
    (v) => v.symbol,
  );
  const currencies: CurrencyV2InstanceType[] = Object.values(data.t.currencies.s).map(currencyV2Instance);
  const currenciesKeyByCurrency: Record<string, CurrencyV2InstanceType> = keyBy(currencies, (v) => v.currency);
  return {
    pswapInstruments,
    spotInstruments,
    instrumentsKeyBySymbol,
    currencies,
    currenciesKeyByCurrency,
  };
};

export const rawSnapshotSpecQueryAtom = atomWithQuery((get) => ({
  queryKey: ['getSnapshotSpec', 'custom'],
  queryFn: async () => {
    try {
      const res = await getSpecSnapshotApi({ showAll: true });
      return snapshotSpecResolver(res.data);
    } catch (err) {
      alert.error(err, { onlyAxiosError: true });
      throw err;
    }
  },
  initialData() {
    const queryClient = get(queryClientAtom);
    const hydratedData = queryClient.getQueryData<SpecSnapshot>(['fetchSpecSnapshotApi']);
    if (hydratedData) return snapshotSpecResolver(hydratedData);
  },
}));

export const rawSnapshotSpecAtom = atom<SnapshotSpec | undefined>((get) => get(rawSnapshotSpecQueryAtom).data);
export const rawInstrumentsKeyBySymbolAtom = atom((get) => get(rawSnapshotSpecAtom)?.instrumentsKeyBySymbol);
export const rawCurrenciesKeyByCurrencyAtom = atom((get) => get(rawSnapshotSpecAtom)?.currenciesKeyByCurrency);

export const snapshotSpecBunja = bunja(() => {
  const { pswapsAtom } = bunja.use(pswapsBunja);
  const snapshotSpecAtom = atom<SnapshotSpec | undefined>((get) => {
    const rawSnapshotSpecData = get(rawSnapshotSpecAtom);
    const pswaps = get(pswapsAtom);
    if (!rawSnapshotSpecData) return;
    if (!pswaps) return rawSnapshotSpecData;
    return {
      ...rawSnapshotSpecData,
      instrumentsKeyBySymbol: Object.fromEntries(
        Object.entries(rawSnapshotSpecData.instrumentsKeyBySymbol).map(([key, instruments]) => {
          const pswap = pswaps[key];
          if (instruments.isSpot) return [key, instruments];
          if (!pswap) return [key, instruments];
          const overriddenInstruments = {
            ...instruments,
            notionalMaxOrderAmount: pswap.notionalMaxOrderAmount,
            notionalMaxPosition: pswap.notionalMaxPosition,
          };
          return [key, overriddenInstruments];
        }),
      ),
    };
  });

  const instrumentsKeyBySymbolAtom = atom((get) => get(snapshotSpecAtom)?.instrumentsKeyBySymbol);
  const currenciesKeyByCurrencyAtom = atom((get) => get(snapshotSpecAtom)?.currenciesKeyByCurrency);

  return { snapshotSpecAtom, instrumentsKeyBySymbolAtom, currenciesKeyByCurrencyAtom };
});
snapshotSpecBunja.debugLabel = 'snapshotSpecBunja';
